/* eslint-disable no-magic-numbers -- source-parity clone-queue thresholds */
// src/store/thunks/poc/cloneQueue/processCloneQueue.ts

import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import { downloadFileFromNetwork } from '@/store/thunks/poc/helia';
import {
  startCloning,
  finishCloning,
  updateCloningProgress,
  interruptCloning,
  markFailed,
  type QueuedFile,
  type CloneQueueState,
} from '@/store/slices/poc/cloneQueueSlice';

// Module-scoped AbortController for the current clone operation.
// Not stored in Redux because AbortController is not serializable.
let currentCloneController: AbortController | null = null;

// Stall detection: tracks how many times each CID has been re-queued
// due to a Bitswap stall, so we can mark as failed after MAX_STALL_RETRIES.
const stallRetryCount = new Map<string, number>();

const STALL_TIMEOUT_MS = 60_000;      // 60s with no chunks → stall
const STALL_CHECK_INTERVAL_MS = 10_000; // check every 10s
const MAX_STALL_RETRIES = 2;
const STALL_RETRY_DELAY_MS = 10_000;   // wait for reconnection before retrying

/**
 * Abort the current clone operation so a higher-priority file can take over.
 * The interrupted file is re-queued (not failed) by the catch handler in processCloneQueue.
 */
export function abortCurrentClone(): void {
  currentCloneController?.abort();
  currentCloneController = null;
}

/**
 * Get next file to clone based on priority:
 * 1. User-initiated (jump queue) - by addedAt order
 * 2. Current six, smallest first (by size)
 * 3. Other sixes, smallest first (by size)
 */
function getNextFile(cloneQueueState: CloneQueueState): QueuedFile | null {
  const { queue, currentSixId } = cloneQueueState;

  if (queue.length === 0) return null;

  // First: user-initiated (by addedAt order)
  const userInitiated = queue.filter(f => f.priority === 'user-initiated');
  if (userInitiated.length > 0) {
    return userInitiated.sort((a, b) => a.addedAt - b.addedAt)[0];
  }

  // Second: current six, smallest first
  if (currentSixId !== null) {
    const currentSixFiles = queue.filter(f => f.sixId === currentSixId);
    if (currentSixFiles.length > 0) {
      return currentSixFiles.sort((a, b) => a.size - b.size)[0];
    }
  }

  // Third: any six, smallest first
  return [...queue].sort((a, b) => a.size - b.size)[0];
}

/**
 * Process the clone queue - downloads one file at a time.
 * Automatically continues to next file after completion.
 */
export const processCloneQueue = createAppAsyncThunk(
  'cloneQueue/process',
  async(_, { dispatch, getState, extra: { pocServices, loggerService } }) => {
    const log = loggerService.createLogger({ namespace: 'six3ro:p2p:clone-queue' });
    const state = getState();

    // Already cloning something - don't start another
    if (state.cloneQueue.currentlyCloning !== null) {
      log.debug('Already cloning a file, skipping');
      return { processed: false, reason: 'already-cloning' };
    }

    const nextFile = getNextFile(state.cloneQueue);
    if (nextFile === null) {
      log.debug('Clone queue empty');
      return { processed: false, reason: 'queue-empty' };
    }

    const fileSizeKB = (nextFile.size / 1024).toFixed(1);
    log.info(`Starting clone: ${nextFile.metadata.name} (${fileSizeKB}KB) for six ${nextFile.sixId}`);
    dispatch(startCloning(nextFile));

    const controller = new AbortController();
    currentCloneController = controller;

    // Stall watchdog: if no chunks arrive within STALL_TIMEOUT_MS, abort
    // with a 'stall' reason so the catch handler can trigger reconnection.
    let lastChunkTime = Date.now();
    const stallWatchdog = setInterval(() => {
      if (Date.now() - lastChunkTime > STALL_TIMEOUT_MS) {
        log.warn(`No Bitswap progress for ${STALL_TIMEOUT_MS}ms on ${nextFile.metadata.name} — aborting`);
        clearInterval(stallWatchdog);
        controller.abort('stall');
      }
    }, STALL_CHECK_INTERVAL_MS);

    try {
      await dispatch(downloadFileFromNetwork({
        sixId: nextFile.sixId,
        cid: nextFile.cid,
        metadata: nextFile.metadata,
        signal: controller.signal,
        onChunkReceived: (bytesLoaded) => {
          lastChunkTime = Date.now();
          dispatch(updateCloningProgress({ bytesLoaded, totalBytes: nextFile.size }));
        },
      })).unwrap();

      clearInterval(stallWatchdog);
      currentCloneController = null;
      stallRetryCount.delete(nextFile.cid);

      log.info(`Clone complete: ${nextFile.metadata.name}`);
      dispatch(finishCloning());

      // Pin in the background — don't block the queue.
      // pins.add() walks the entire DAG which is slow for large files.
      // No automatic GC runs in Helia with IDB blockstore, so the blocks
      // are safe between finishCloning and pin completion.
      const heliaNode = pocServices.p2pStackManagerService.getHeliaNode();
      if (heliaNode !== null) {
        pocServices.fileStoreService.pinFile(heliaNode, nextFile.cid)
          .then(() => { log.info(`Pinned: ${nextFile.cid}`); })
          .catch((pinErr: unknown) => { log.warn(`Pinning failed for ${nextFile.cid}:`, pinErr); });
      }

      // Process next item in queue
      void dispatch(processCloneQueue());

      return { processed: true, cid: nextFile.cid, success: true };

    } catch (error) {
      clearInterval(stallWatchdog);
      currentCloneController = null;

      // Interrupted by abortCurrentClone() or stall watchdog.
      // Check our own controller's signal (not error.name) because error wrapping
      // in getFileData and networkFileThunks destroys the AbortError identity.
      if (controller.signal.aborted) {
        const isStall = controller.signal.reason === 'stall';

        if (isStall) {
          const retries = stallRetryCount.get(nextFile.cid) ?? 0;

          if (retries >= MAX_STALL_RETRIES) {
            log.error(`Clone stalled ${retries + 1} times: ${nextFile.metadata.name} — marking failed`);
            stallRetryCount.delete(nextFile.cid);
            dispatch(markFailed({ cid: nextFile.cid, error: 'Bitswap stall: no data received after reconnection retries' }));
            void dispatch(processCloneQueue());
            return { processed: true, cid: nextFile.cid, success: false, error: 'stall-exhausted' };
          }

          stallRetryCount.set(nextFile.cid, retries + 1);
          log.warn(`Clone stalled: ${nextFile.metadata.name} — reconnecting (attempt ${retries + 1}/${MAX_STALL_RETRIES})`);

          // Signal the service layer to handle the dead connection
          pocServices.p2pStackManagerService.reportBitswapStall(nextFile.sixId);

          dispatch(interruptCloning());

          // Delay before retry to allow reconnection to establish
          setTimeout(() => { void dispatch(processCloneQueue()); }, STALL_RETRY_DELAY_MS);
          return { processed: true, cid: nextFile.cid, success: false, error: 'stall-reconnecting' };
        }

        // Normal interrupt (priority change) — re-queue immediately
        log.info(`Clone interrupted: ${nextFile.metadata.name} — re-queuing`);
        dispatch(interruptCloning());
        void dispatch(processCloneQueue());
        return { processed: true, cid: nextFile.cid, success: false, error: 'interrupted' };
      }

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      log.error(`Clone failed: ${nextFile.metadata.name} - ${errorMessage}`);
      dispatch(markFailed({ cid: nextFile.cid, error: errorMessage }));

      // Continue with next item despite failure
      void dispatch(processCloneQueue());

      return { processed: true, cid: nextFile.cid, success: false, error: errorMessage };
    }
  }
);
